import { createFileRoute, notFound } from "@tanstack/react-router";
import { getGame } from "@/data/games";
import type { PackId } from "@/data/quotes";
import { WhoSaidThis } from "@/components/games/WhoSaidThis";
import { FinishQuoteGame } from "@/components/games/FinishQuoteGame";
import { ScrambleGame } from "@/components/games/ScrambleGame";
import { TrueOrFakeGame } from "@/components/games/TrueOrFakeGame";
import { TrueOrFalseGame } from "@/components/games/TrueOrFalseGame";
import { QuoteWordle, type WordleMode } from "@/components/games/QuoteWordle";
import { WordBloom } from "@/components/games/WordBloom";

interface PlaySearch {
  live?: "1";
  pack?: PackId;
  level?: number;
  mode?: WordleMode;
}

export const Route = createFileRoute("/games/$slug/play")({
  beforeLoad: ({ params }) => {
    if (!getGame(params.slug)) throw notFound();
  },
  validateSearch: (s: Record<string, unknown>): PlaySearch => ({
    live: s.live === "1" ? "1" : undefined,
    pack: typeof s.pack === "string" ? (s.pack as PackId) : undefined,
    level: typeof s.level === "number" ? s.level : typeof s.level === "string" ? Number(s.level) : undefined,
    mode: s.mode === "daily" || s.mode === "endless" ? s.mode : undefined,
  }),
  head: ({ params }) => {
    const g = getGame(params.slug)!;
    const url = `/games/${g.slug}/play`;
    return {
      meta: [
        { title: `Play ${g.name} — Mr. B Quotes` },
        { name: "description", content: g.seoDescription },
        { property: "og:title", content: `Play ${g.name}` },
        { property: "og:description", content: g.seoDescription },
        { property: "og:url", content: url },
        { name: "robots", content: "noindex" }, // play route is the app; hub is the indexable page
      ],
      links: [{ rel: "canonical", href: `/games/${g.slug}` }],
    };
  },
  component: PlayRoute,
});

function PlayRoute() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const pack = (search.pack ?? "all") as PackId;
  const level = search.level ?? 1;
  const mode: WordleMode = search.mode ?? "endless";
  switch (slug) {
    case "who-said-this":   return <WhoSaidThis pack={pack} />;
    case "finish-quote":    return <FinishQuoteGame pack={pack} />;
    case "scramble":        return <ScrambleGame level={level} />;
    case "true-or-fake":    return <TrueOrFakeGame pack={pack} />;
    case "true-or-false":   return <TrueOrFalseGame pack={pack} />;
    case "quote-wordle":    return <QuoteWordle mode={mode} />;
    case "word-bloom":      return <WordBloom startLevel={Math.max(1, level)} />;
    default:                return null;
  }
}
